/**
 * WalkHereRoute — the in-Canvas preview for the right-click "Walk here" flow
 * (ENG-1050): the planned navmesh polyline plus a marker on the destination.
 *
 * This is the immediate answer to the click. Generation takes seconds, so
 * without a preview the gesture would produce nothing visible until the walk
 * starts, which reads as a dropped click.
 *
 * Imperative THREE, cold-rebuilt inside the walkHereState subscription — no
 * React state for the geometry (the route is user-cadence state owned by the
 * store). Always-on-top recipe (renderOrder 9999, depthTest/depthWrite off,
 * transparent so it lands in the late pass) so it draws over a gaussian-splat
 * environment, same as WaypointPickerOverlay and NavDebugOverlay.
 *
 * Deliberately its OWN group, not the wizard's __WAYPOINT_MARKERS__: the two
 * flows are independent, and sharing the group would let either one clear the
 * other's markers.
 *
 * The one useFrame here runs only while a generation is in flight, and only
 * writes to an existing marker's scale — no allocation, and nothing to do on an
 * idle frame beyond a ref check.
 */
import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { walkHereState } from '../lib/walkHereState'
import { navSettingsStore } from '../lib/navSettingsStore'
import { avatarFloor } from '../lib/avatarFloor'
import { WAYPOINT_COLORS } from '../lib/waypointColors'
import { buildAnchorGizmo } from './routeAnchorGizmo'

const LIVE_PHASES = new Set(['planning', 'generating', 'walking', 'arrived'])

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
}

export default function WalkHereRoute() {
  const scene = useThree(s => s.scene)
  const groupRef = useRef(null)
  const markerRef = useRef(null)
  const pulsingRef = useRef(false)
  const tRef = useRef(0)

  useEffect(() => {
    const teardown = () => {
      const g = groupRef.current
      if (!g) return
      g.traverse((o) => {
        o.geometry?.dispose?.()
        o.material?.dispose?.()
      })
      scene.remove(g)
      groupRef.current = null
      markerRef.current = null
      if (typeof window !== 'undefined') window.__WALK_HERE_ROUTE__ = null
    }

    let lastRoute, lastPhase, lastShow
    const rebuild = () => {
      const { route, destination, phase, clamped } = walkHereState.getState()
      // The NAVMESH row's "Show walk path" toggle. Defaults ON: this preview is
      // the immediate answer to the click, drawn before the generation returns.
      const show = navSettingsStore.getState().show.walkPath
      const live = show && LIVE_PHASES.has(phase) && route?.path?.length >= 2
      // The store emits on message-only transitions too; only rebuild when the
      // geometry or its live-ness actually changed (this allocates a geometry).
      if (route === lastRoute && phase === lastPhase && show === lastShow) return
      lastRoute = route
      lastPhase = phase
      lastShow = show
      teardown()
      pulsingRef.current = false
      if (!live) return

      const floorY = avatarFloor.getY() // world floor, not the bake's own floorY
      const group = new THREE.Group()
      group.renderOrder = 9999

      // Route polyline, drawn at the WORLD floor rather than each point's own y:
      // a planned path carries the navmesh's own node heights, which are in the
      // bake's space and can be metres from the floor the avatar stands on.
      const pts = route.path.map(p => new THREE.Vector3(p[0], floorY + 0.05, p[2]))
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineBasicMaterial({
          // Amber when the goal was pulled in: the line does not reach where the
          // user clicked, and the colour says so before the walk starts.
          color: (clamped ? WAYPOINT_COLORS.routeStraight : WAYPOINT_COLORS.routeNavmesh).num,
          depthTest: false, depthWrite: false, transparent: true,
        }),
      )
      line.renderOrder = 9999
      group.add(line)

      // The root points the kimodo request will carry, and the facing asked for
      // at each — the same gizmo the wizard's route preview draws, because it is
      // the same constraint being previewed.
      for (const o of buildAnchorGizmo(route.headings, floorY)) group.add(o)

      // Destination ring, flat on the floor — the "you clicked here" anchor.
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.16, 0.22, 32),
        new THREE.MeshBasicMaterial({
          color: (clamped ? WAYPOINT_COLORS.routeStraight : WAYPOINT_COLORS.point).num,
          depthTest: false, depthWrite: false, transparent: true, opacity: 0.95,
          side: THREE.DoubleSide,
        }),
      )
      const d = destination ?? route.path[route.path.length - 1]
      ring.position.set(d[0], floorY + 0.06, d[2])
      ring.rotation.x = -Math.PI / 2
      ring.renderOrder = 9999
      group.add(ring)

      scene.add(group)
      groupRef.current = group
      markerRef.current = ring
      // Pulse only while we're waiting on kimodo, and never against the user's
      // reduced-motion preference.
      pulsingRef.current = phase === 'generating' && !prefersReducedMotion()
      tRef.current = 0
      if (typeof window !== 'undefined') window.__WALK_HERE_ROUTE__ = group
    }

    // Both stores drive it: the flow's phase, and whether the layer is shown.
    const unsub = walkHereState.subscribe(rebuild)
    const unsubNav = navSettingsStore.subscribe(rebuild)
    rebuild()
    return () => { unsub(); unsubNav(); teardown() }
  }, [scene])

  // "Waiting" breath on the destination ring — the 3D half of the generating
  // state (the pill is the DOM half).
  useFrame((_, dt) => {
    if (!pulsingRef.current) return
    const ring = markerRef.current
    if (!ring) return
    tRef.current += dt
    const s = 1 + 0.14 * Math.sin(tRef.current * 3.2)
    ring.scale.set(s, s, 1)
  })

  return null
}
